import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface PWAState {
  isInstallable: boolean;
  isInstalled: boolean;
  isOnline: boolean;
  isUpdateAvailable: boolean;
  installPrompt: BeforeInstallPromptEvent | null;
}

interface PWAActions {
  install: () => Promise<void>;
  showInstallPrompt: () => void;
  dismissInstallPrompt: () => void;
  checkForUpdate: () => Promise<void>;
  reloadApp: () => void;
}

export const usePWA = (): PWAState & PWAActions => {
  const [state, setState] = useState<PWAState>({
    isInstallable: false,
    isInstalled: false,
    isOnline: navigator.onLine,
    isUpdateAvailable: false,
    installPrompt: null,
  });

  const checkInstallStatus = useCallback(() => {
    const isInstalled = 
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      (window.navigator as any).standalone === true;
    
    setState(prev => ({ ...prev, isInstalled }));
  }, []);

  const handleBeforeInstallPrompt = useCallback((e: Event) => {
    e.preventDefault();
    const installPrompt = e as BeforeInstallPromptEvent;
    setState(prev => ({ 
      ...prev, 
      isInstallable: true, 
      installPrompt 
    }));
  }, []);

  const handleAppInstalled = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      isInstallable: false, 
      isInstalled: true, 
      installPrompt: null 
    }));
  }, []);

  const handleOnlineStatus = useCallback(() => {
    setState(prev => ({ ...prev, isOnline: navigator.onLine }));
  }, []);

  const install = useCallback(async (): Promise<void> => {
    if (!state.installPrompt) {
      throw new Error('Install prompt is not available');
    }

    try {
      await state.installPrompt.prompt();
      const choiceResult = await state.installPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        setState(prev => ({ 
          ...prev, 
          isInstallable: false, 
          installPrompt: null 
        }));
      }
    } catch (error) {
      console.error('Failed to install app:', error);
      throw error;
    }
  }, [state.installPrompt]);

  const showInstallPrompt = useCallback(() => {
    if (state.installPrompt) {
      install().catch(console.error);
    }
  }, [install, state.installPrompt]);

  const dismissInstallPrompt = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      isInstallable: false, 
      installPrompt: null 
    }));
  }, []);

  const checkForUpdate = useCallback(async (): Promise<void> => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.update();
        }
      } catch (error) {
        console.error('Failed to check for updates:', error);
        throw error;
      }
    }
  }, []);

  const reloadApp = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    checkInstallStatus();

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'UPDATE_AVAILABLE') {
          setState(prev => ({ ...prev, isUpdateAvailable: true }));
        }
      });

      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration && registration.waiting) {
          setState(prev => ({ ...prev, isUpdateAvailable: true }));
        }
      });
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, [checkInstallStatus, handleBeforeInstallPrompt, handleAppInstalled, handleOnlineStatus]);

  return {
    ...state,
    install,
    showInstallPrompt,
    dismissInstallPrompt,
    checkForUpdate,
    reloadApp,
  };
};

export const isPWASupported = (): boolean => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

export const isRunningAsPWA = (): boolean => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.matchMedia('(display-mode: fullscreen)').matches ||
         (window.navigator as any).standalone === true;
};

export const getPWADisplayMode = (): string => {
  if (window.matchMedia('(display-mode: fullscreen)').matches) return 'fullscreen';
  if (window.matchMedia('(display-mode: standalone)').matches) return 'standalone';
  if (window.matchMedia('(display-mode: minimal-ui)').matches) return 'minimal-ui';
  return 'browser';
};

export const canShare = (): boolean => {
  return 'share' in navigator;
};

export const shareContent = async (data: ShareData): Promise<void> => {
  if (!canShare()) {
    throw new Error('Web Share API is not supported');
  }
  
  try {
    await navigator.share(data);
  } catch (error) {
    if ((error as Error).name !== 'AbortError') {
      throw error;
    }
  }
};
